import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Banner,
  BlockStack,
  Button,
  LegacyCard,
  Layout,
  PageActions,
  Spinner,
  Text
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { authService } from '../services/authService';
import { ordersService } from '../services/ordersService';
import { useOrderQuote } from '../hooks/useOrderQuote';
import StoryEntryCard from '../components/orders/create/StoryEntryCard';
import CustomerCard from '../components/orders/create/CustomerCard';
import ShippingPaymentCard from '../components/orders/create/ShippingPaymentCard';
import OrderSummaryCard from '../components/orders/create/OrderSummaryCard';
import { buildOrderPayload, buildQuoteRequest } from '../utils/storefrontOrder/payload';
import { validateForm } from '../utils/storefrontOrder/validation';

const PRICING_CHANGED_PATTERN = 'Shopify draft order pricing changed';

function makeEntry(counterRef) {
  return {
    key: `e${counterRef.current++}`,
    story: null,
    variant: null,
    languageCode: 'en',
    kidName: '',
    photos: []
  };
}

function makeInitialForm(counterRef) {
  return {
    entries: [makeEntry(counterRef)],
    customer: { name: '', phone: '', email: '', address: '', city: '' },
    shippingValue: 'regular',
    paymentMethod: 'cod',
    note: '',
    discountInput: '',
    appliedDiscount: ''
  };
}

/**
 * Dashboard order-creation page mirroring the Shopify storefront form.
 * Renders only the body; App.js wraps routes in a Page, so this component
 * provides a two-column Layout instead of its own Page.
 *
 * @param {Object} props
 * @param {Function} [props.setToastMessage]
 * @param {Function} [props.setToastActive]
 * @returns {JSX.Element}
 */
export default function OrderCreatePage({ setToastMessage, setToastActive }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const entryKeyRef = useRef(1);

  const [initial] = useState(() => makeInitialForm(entryKeyRef));
  const [entries, setEntries] = useState(initial.entries);
  const [customer, setCustomer] = useState(initial.customer);
  const [shippingValue, setShippingValue] = useState(initial.shippingValue);
  const [paymentMethod, setPaymentMethod] = useState(initial.paymentMethod);
  const [note, setNote] = useState(initial.note);
  const [discountInput, setDiscountInput] = useState(initial.discountInput);
  const [appliedDiscount, setAppliedDiscount] = useState(initial.appliedDiscount);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  const [submitState, setSubmitState] = useState('idle');
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  // Customer errors stay hidden until a submit is attempted (or a POST
  // fails); afterwards the whole card shows its validation errors.
  const [customerErrorsForced, setCustomerErrorsForced] = useState(false);

  const submittingRef = useRef(false);

  const country = authService.getActiveCountry();
  const isEgypt = country === 'EG';

  // Load the story catalog on mount. Retry uses refresh:false: after a
  // failure the server cache was never populated, so a plain refetch still
  // hits the backend.
  const loadCatalog = useCallback(async ({ refresh = false } = {}) => {
    setCatalogLoading(true);
    try {
      const data = await ordersService.getStoryCatalog({ refresh });
      setCatalog(data || []);
      setCatalogError(null);
    } catch (err) {
      setCatalogError(err.message || 'Failed to load story catalog.');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Quote request: only when at least one entry has a chosen variant.
  // Memoized on the meaningful fields so the quote hook's effect does not
  // re-arm on every render.
  const request = useMemo(() => {
    const quoteEntries = entries.filter(entry => entry.story && entry.variant);
    if (quoteEntries.length === 0) {
      return null;
    }
    return buildQuoteRequest({
      entries: quoteEntries,
      shippingValue,
      discountCode: appliedDiscount
    });
  }, [entries, shippingValue, appliedDiscount]);

  const { quote, status, error: quoteError, refresh: refreshQuote } =
    useOrderQuote(request);

  // A quote that reports the discount code as invalid must not be sent
  // again: clear the applied code so the next request omits it.
  const requoteAfterInvalidRef = useRef(false);
  useEffect(() => {
    if (quote && quote.discount_code && quote.discount_code.valid === false) {
      if (appliedDiscount) {
        requoteAfterInvalidRef.current = true;
        setAppliedDiscount('');
      }
    } else if (quote) {
      requoteAfterInvalidRef.current = false;
    }
  }, [quote, appliedDiscount]);

  // Once the invalid code is cleared, re-request the quote without it
  // exactly once, bypassing the fingerprint cache so the service actually
  // sees a request with no discount_code.
  useEffect(() => {
    if (
      requoteAfterInvalidRef.current &&
      quote &&
      quote.discount_code &&
      quote.discount_code.valid === false &&
      !appliedDiscount
    ) {
      requoteAfterInvalidRef.current = false;
      refreshQuote().catch(() => {});
    }
  }, [quote, appliedDiscount, refreshQuote]);

  const validation = useMemo(
    () => validateForm({ entries, customer, paymentMethod }),
    [entries, customer, paymentMethod]
  );

  const formDisabled = !isEgypt || catalogLoading || Boolean(catalogError);

  const canSubmit =
    isEgypt &&
    !catalogError &&
    !catalogLoading &&
    validation.valid &&
    status === 'ready' &&
    submitState !== 'submitting';

  const handleEntryChange = useCallback((key, nextEntry) => {
    setEntries(current =>
      current.map(entry => (entry.key === key ? nextEntry : entry))
    );
  }, []);

  const handleEntryRemove = useCallback(key => {
    setEntries(current => current.filter(entry => entry.key !== key));
  }, []);

  const handleAddEntry = useCallback(() => {
    setEntries(current => [...current, makeEntry(entryKeyRef)]);
  }, []);

  const handleCustomerChange = useCallback((field, value) => {
    setCustomer(current => ({ ...current, [field]: value }));
  }, []);

  const handleShippingChange = useCallback((field, value) => {
    if (field === 'shippingValue') {
      setShippingValue(value);
    } else if (field === 'paymentMethod') {
      setPaymentMethod(value);
    } else if (field === 'note') {
      setNote(value);
    }
  }, []);

  const handleApplyDiscount = useCallback(() => {
    setAppliedDiscount(discountInput.trim());
  }, [discountInput]);
  const handleRemoveDiscount = useCallback(() => {
    setAppliedDiscount('');
    setDiscountInput('');
    // Clearing appliedDiscount rebuilds the quote request without the code,
    // so useOrderQuote re-quotes automatically.
  }, []);

  const handleRetryQuote = useCallback(() => {
    refreshQuote().catch(() => {
      // Quote error state is already reflected by the hook.
    });
  }, [refreshQuote]);

  const resetForm = useCallback(() => {
    setEntries([makeEntry(entryKeyRef)]);
    setCustomer({ name: '', phone: '', email: '', address: '', city: '' });
    setShippingValue('regular');
    setPaymentMethod('cod');
    setNote('');
    setDiscountInput('');
    setAppliedDiscount('');
    setSubmitState('idle');
    setSubmitError(null);
    setResult(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    // A submit attempt reveals all customer validation errors, even when
    // the synchronous guard below bails out early.
    setCustomerErrorsForced(true);
    // Synchronous guard: rapid double clicks must not create two orders.
    if (submittingRef.current || !canSubmit) {
      return;
    }
    submittingRef.current = true;
    setSubmitState('submitting');
    setSubmitError(null);

    try {
      // Always submit against a fresh quote, never a cached one.
      const freshQuote = await refreshQuote();
      if (!freshQuote) {
        throw new Error('Unable to calculate order pricing');
      }

      const payload = buildOrderPayload({
        entries,
        customer,
        shippingValue,
        paymentMethod,
        note,
        quote: freshQuote,
        userEmail: (user && user.email) || ''
      });

      const created = await ordersService.createStorefrontOrder(payload);
      setResult(created);
      setSubmitState('success');
      if (setToastMessage) {
        setToastMessage('Order created in Shopify.');
      }
      if (setToastActive) {
        setToastActive(true);
      }
    } catch (err) {
      setSubmitState('idle');
      const message = (err && err.message) || '';
      if (message.includes(PRICING_CHANGED_PATTERN)) {
        setSubmitError('Pricing changed, please review the new total');
        // Re-quote so the summary shows the new pricing.
        refreshQuote().catch(() => {});
      } else if (typeof err?.status === 'number' && err.status >= 400 && err.status < 500) {
        // The backend rejected the payload: surface its validation message
        // and details rather than the ambiguous "may not have been created"
        // copy (a 4xx means the order was definitively not created).
        let banner = err.message || 'Order could not be created.';
        if (err.details) {
          const detailsText =
            typeof err.details === 'string'
              ? err.details
              : JSON.stringify(err.details);
          banner += ` ${detailsText}`;
        }
        setSubmitError(banner);
      } else {
        setSubmitError(
          'Order may not have been created. Check the Orders list before retrying.'
        );
      }
    } finally {
      submittingRef.current = false;
    }
  }, [
    canSubmit,
    refreshQuote,
    entries,
    customer,
    shippingValue,
    paymentMethod,
    note,
    user,
    setToastMessage,
    setToastActive
  ]);

  // ==================== SUCCESS SCREEN ====================
  if (submitState === 'success' && result) {
    return (
      <Layout>
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <BlockStack gap="400" align="center">
                <Text variant="headingLg" as="h2">
                  Order #{
                    result.shopify_order_number !== undefined
                      ? result.shopify_order_number
                      : ''
                  }{' '}
                  was created in Shopify. It will appear in the Orders list in a
                  moment.
                </Text>
                <BlockStack gap="200">
                  <Button variant="primary" onClick={resetForm}>
                    Create another
                  </Button>
                  <Button onClick={() => navigate('/orders')}>Back to orders</Button>
                </BlockStack>
              </BlockStack>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    );
  }

  // ==================== FORM ====================
  const entriesMarkup = entries.map((entry, index) => {
    const touched = Boolean(entry.story || entry.kidName ||
      (entry.photos && entry.photos.length > 0));
    return (
      <StoryEntryCard
        key={entry.key}
        entry={entry}
        index={index}
        catalog={catalog}
        onChange={nextEntry => handleEntryChange(entry.key, nextEntry)}
        onRemove={() => handleEntryRemove(entry.key)}
        // Stories can be removed down to zero; validateForm then blocks
        // submission with "Add at least one story to the order."
        canRemove
        showErrors={touched}
      />
    );
  });

  return (
    <Layout>
      {catalogError ? (
        <Layout.Section>
          <Banner
            tone="critical"
            action={{ content: 'Retry', onAction: () => loadCatalog({ refresh: false }) }}
          >
            <Text as="p">{catalogError}</Text>
          </Banner>
        </Layout.Section>
      ) : null}

      {!isEgypt ? (
        <Layout.Section>
          <Banner tone="warning">
            <Text as="p">Order creation is available for Egypt only</Text>
          </Banner>
        </Layout.Section>
      ) : null}

      <Layout.Section>
        <BlockStack gap="500">
          {catalogLoading ? (
            <InlineSpinner />
          ) : (
            entriesMarkup
          )}
          <div>
            <Button onClick={handleAddEntry} disabled={formDisabled}>
              Add story
            </Button>
          </div>

          <CustomerCard
            customer={customer}
            errors={validation.customerErrors}
            onChange={handleCustomerChange}
            disabled={formDisabled}
            forceShowErrors={customerErrorsForced}
          />

          <ShippingPaymentCard
            shippingValue={shippingValue}
            paymentMethod={paymentMethod}
            note={note}
            city={customer.city}
            onChange={handleShippingChange}
            disabled={formDisabled}
          />
        </BlockStack>
      </Layout.Section>

      <Layout.Section variant="oneThird">
        <OrderSummaryCard
          entries={entries}
          quote={quote}
          status={status}
          error={quoteError}
          discountInput={discountInput}
          onDiscountChange={setDiscountInput}
          onApplyDiscount={handleApplyDiscount}
          onRemoveDiscount={handleRemoveDiscount}
          canSubmit={canSubmit}
          submitting={submitState === 'submitting'}
          submitError={submitError}
          onSubmit={handleSubmit}
          onRetryQuote={handleRetryQuote}
          disabled={formDisabled}
        />
      </Layout.Section>

      <Layout.Section>
        <PageActions
          secondaryActions={[
            {
              content: 'Back to orders',
              onAction: () => navigate('/orders')
            }
          ]}
        />
      </Layout.Section>
    </Layout>
  );
}

// Small inline loading indicator used while the catalog loads.
function InlineSpinner() {
  return (
    <LegacyCard>
      <LegacyCard.Section>
        <BlockStack gap="200" align="center">
          <Spinner accessibilityLabel="Loading stories" size="small" />
          <Text as="p" tone="subdued">
            Loading stories…
          </Text>
        </BlockStack>
      </LegacyCard.Section>
    </LegacyCard>
  );
}
